import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Stack } from 'rsuite';
import { lookupAddressAPN } from '../../api/apiAccess';
import { AutocompleteWithErrorStatus } from './AutocompleteWithErrorStatus';

const MINIMUM_SEARCH_TERM_LENGTH = 3;
const DEBOUNCE_DELAY = 500; // 500ms

const CityRestrictedAddressLookup = ({
  name,
  label,
  validationRule,
  hasError,
  errorMessage,
  hideErrorIcon = false,
  disabled = false,
  onTextChanged = () => {},
  onItemSelected = () => {},
  ...rest
}) => {
  const containerRef = useRef();
  const [menuWidth, setMenuWidth] = useState('0px');
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressAPNDataset, setAddressAPNDataset] = useState([]);
  const [resizeTrigger, setResizeTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleResize = () => {
    setResizeTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    // This is required to prevent a ResizeObserver bug
    // It ensures that the autocomplete suggestions menu is can not attempt to
    // expand beyond the size of the input field
    const newWidth = containerRef.current.clientWidth;
    setMenuWidth(`${newWidth}px`);
  }, [containerRef, resizeTrigger]);

  const debouncedSearch = useCallback(
    async (value) => {
      onTextChanged();
      if (value === undefined || value.length < MINIMUM_SEARCH_TERM_LENGTH) {
        setAddressOptions([]);
        return;
      }
      setLoading(true);
      const { data } = await lookupAddressAPN(value);
      setAddressAPNDataset(data);
      setAddressOptions([...new Set(Object.keys(data))]);
      setLoading(false);
    },
    [onTextChanged]
  );

  const onSearchTermChanged = useCallback(
    (value) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        debouncedSearch(value);
      }, DEBOUNCE_DELAY);
    },
    [debouncedSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const onAddressSelected = async (item) => {
    let result = null;
    let { address } = item;
    let matchingAPN = addressAPNDataset[address];
    if (matchingAPN) {
      result = {
        address: address,
        apn: matchingAPN,
      };
    }
    onItemSelected(result);
  };

  const AutocompleteWithErrorStateAndProps = useCallback(
    (props) => (
      <AutocompleteWithErrorStatus
        {...props}
        showError={hasError}
        errorMessage={errorMessage}
        hideErrorIcon={hideErrorIcon}
        isLoading={props.loading === 'true'}
      />
    ),
    [hasError, errorMessage, hideErrorIcon]
  );

  return (
    <Form.Group
      controlId={name}
      {...rest.xs}
      data-testid="city-restricted-address-lookup"
    >
      <Stack direction="row">
        <Form.ControlLabel>{label}</Form.ControlLabel>
      </Stack>
      <Stack ref={containerRef}>
        <Form.Control
          className={disabled === true ? 'disabled-input' : null}
          style={{ width: menuWidth }}
          name={name}
          rule={validationRule}
          accepter={AutocompleteWithErrorStateAndProps}
          onChange={onSearchTermChanged}
          onSelect={onAddressSelected}
          data={addressOptions}
          disabled={disabled}
          loading={loading ? 'true' : 'false'}
          {...rest}
        />
      </Stack>
    </Form.Group>
  );
};

export default CityRestrictedAddressLookup;
